import React, { useEffect, useRef, useState } from 'react';
import { Box, Inscription, LineChart, ShimmerBox } from '@common-fe/common-fe';

import theme from '@/styles/theme';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';

import usePlanBalanceChartData from './hooks/usePlanBalanceChartData';

const DEFAULT_CHART_WIDTH = 410;

interface Props {
  planId: number;
  accountId?: number;
}

const BankingBalancePlanChart: React.FC<Props> = ({ planId, accountId }) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(DEFAULT_CHART_WIDTH);

  const {
    values,
    hints,
    labels,
    isLoading,
    isWithNegativeValues,
    isEmpty,
  } = usePlanBalanceChartData({accountId});

  useEffect(() => {
    const handleResize = () => {
      if (parentRef.current) {
        setChartWidth(parentRef.current.offsetWidth);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Box
      data-testid={`plan-chart_${planId}`}
      gap="spacing8"
      ref={parentRef}
      height="fit-content"
    >
      <Inscription weight="bold" size="14px" lineHeight="20px">
        Balance over time
      </Inscription>

      <Box>
        {isLoading ? (
          <ShimmerBox width={`${chartWidth}px`} height="200px" />
        ) : (
          <>
          {isEmpty ? (
            <Box 
              height="200px"
              width="100%"
              background={{ color: 'module' }}
              round="moduleRound"
              align="center"
              justify="center"
            >
              <Inscription color="textSecondary">
                There are no data yet
              </Inscription>
            </Box>
          ) : (
            <LineChart
              fontFamily={whiteLabelingConfig.font}
              width={chartWidth}
              key={chartWidth}
              values={values}
              tips={hints}
              labels={labels}
              lineColor={theme.colors.chart}
              showHorizontalLines
              showNegativeMode={isWithNegativeValues}
              centerLabels
              height={200}
              paddingRight={22}
            />
          )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default BankingBalancePlanChart;
